import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Title,
  Tabs,
  Button,
  Modal,
  TextInput,
  Stack,
  Text,
  Image,
  Box,
  Tooltip,
  Group,
} from "@mantine/core";
import {
  IconHome,
  IconClock,
  IconCheck,
  IconArrowRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import usePermissions from "../../hooks/usePermissions";
import { Employee } from "../../types/salon";
import { BusinessInfo } from "../../types/context";
import {
  TimeTrackingState,
  TimeTrackingStatusResponse,
  TimesheetData,
} from "../../types/timeTracking";
import { CreateBusinessRequest } from "../../types/rollfi";
import { notifyError } from "../../utils/notify";
import { usePayrollPeriod } from "../../hooks/usePayrollPeriod";
import { usePayrollUserSync } from "../../hooks/usePayrollUserSync";
import { canSetUpPayroll, canUsePayroll } from "../../utils/payrollAccess";
import PayrollAccessRestricted from "./PayrollAccessRestricted";
import PayrollOverviewTab from "./PayrollOverviewTab";
import PayrollTimesheetTab from "./PayrollTimesheetTab";
import styles from "./PayrollPage.module.css";

interface SalonContextValue {
  currentUser: Employee | null;
  businessInfo: BusinessInfo | null;
  employees: Employee[];
  isLoading: boolean;
}

export type EmployeeStatusMap = Map<string, TimeTrackingState>;

const DEFAULT_STATUS: TimeTrackingState = {
  isClockedIn: false,
  isOnBreak: false,
};

export default function PayrollPage() {
  const { isMobile } = useResponsive();
  const salonContext = useSalon() as unknown as SalonContextValue;
  const {
    currentUser,
    businessInfo,
    employees,
    isLoading: salonLoading,
    setBusinessInfo,
    setCurrentUser,
    setEmployees,
  } = salonContext as SalonContextValue & {
    // The context setter is a useState dispatch — functional updates are the
    // required form for merges (never replace with a captured object).
    setBusinessInfo: React.Dispatch<React.SetStateAction<BusinessInfo | null>>;
    setCurrentUser: (user: Employee) => void;
    setEmployees: (employees: Employee[]) => void;
  };
  const {
    getTimeTrackingStatus,
    getPayPeriodTimeCards,
    updateSalon,
    createRollfiBusiness,
    updateStaff,
  } = useApp();
  const { syncPayrollUsers, isSyncing: isSyncingUsers } = usePayrollUserSync();
  const perms = usePermissions();
  const payrollPerms = perms?.payroll;
  const settingsPayrollPerms = perms?.settingsPayroll;

  const [activeTab, setActiveTab] = useState<string | null>("overview");

  // Marketing page state
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [legalBusinessName, setLegalBusinessName] = useState("");
  const [isCreatingBusiness, setIsCreatingBusiness] = useState(false);

  // Shared employee status state
  const [employeeStatuses, setEmployeeStatuses] = useState<EmployeeStatusMap>(
    new Map(),
  );
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const hasFetchedStatuses = useRef(false);
  const hasSyncedUsers = useRef(false);

  // Shared timesheet data state
  const [timesheetData, setTimesheetData] = useState<TimesheetData>({
    timeCards: [],
  });
  const [isLoadingTimesheet, setIsLoadingTimesheet] = useState(true);
  // Track which payPeriodId we've fetched to avoid re-fetching same period
  const fetchedPayPeriodId = useRef<string | null>(null);

  const typedBusinessInfo = businessInfo as BusinessInfo | null;
  const typedEmployees = employees as Employee[];

  // Get current pay period
  const payrollPeriod = usePayrollPeriod(typedBusinessInfo);

  const isOwnerOrManager =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";

  // Owners of an Active location without payroll see the setup (marketing)
  // page; everyone else needs payroll set up for the location and for them
  // (utils/payrollAccess), or the RollFi calls below fail.
  const payrollAccessInput = { currentUser, businessInfo: typedBusinessInfo };
  const showMarketingPage = canSetUpPayroll(payrollAccessInput);
  const hasAccess = canUsePayroll(payrollAccessInput);

  // Filter employees who have a RollFi user ID attached
  const eligibleEmployees = (typedEmployees || []).filter(
    (emp) => emp.rollfi__user_id,
  );

  // Fetch status for all eligible employees
  const fetchAllStatuses = useCallback(async () => {
    if (eligibleEmployees.length === 0) {
      setIsLoadingStatuses(false);
      return;
    }

    setIsLoadingStatuses(true);

    try {
      const statusPromises = eligibleEmployees.map(async (emp) => {
        try {
          const status: TimeTrackingStatusResponse =
            await getTimeTrackingStatus(
              emp.rollfi__user_id!,
              typedBusinessInfo?.rollfi__company_id || "",
            );
          return {
            employeeId: emp.id,
            status: {
              isClockedIn: status.isClockedIn,
              isOnBreak: status.isOnBreak,
              clockInTime: status.clockInTime,
              breakStartTime: status.breakStartTime,
            } as TimeTrackingState,
          };
        } catch (err) {
          console.error(`Failed to fetch status for employee ${emp.id}:`, err);
          return {
            employeeId: emp.id,
            status: DEFAULT_STATUS,
          };
        }
      });

      const results = await Promise.all(statusPromises);
      const statusMap = new Map<string, TimeTrackingState>(
        results.map((r) => [r.employeeId, r.status]),
      );

      setEmployeeStatuses(statusMap);
    } catch (err) {
      console.error("Error fetching employee statuses:", err);
    } finally {
      setIsLoadingStatuses(false);
    }
  }, [eligibleEmployees, getTimeTrackingStatus]);

  // Sync RollFi users on page load, then fetch statuses
  useEffect(() => {
    if (salonLoading || hasSyncedUsers.current) return;
    if (!typedBusinessInfo?.rollfi__company_id) {
      setIsLoadingStatuses(false);
      return;
    }

    hasSyncedUsers.current = true;

    const syncThenFetch = async () => {
      // Sync users first so rollfi__user_id is populated on employees
      await syncPayrollUsers({ silent: true });

      // After sync, statuses will be fetched by the next effect
      // (eligibleEmployees will update once employees have rollfi__user_id)
    };

    syncThenFetch();
  }, [salonLoading, typedBusinessInfo?.rollfi__company_id, syncPayrollUsers]);

  // Fetch statuses after user sync completes (eligibleEmployees updates)
  useEffect(() => {
    if (salonLoading || isSyncingUsers || hasFetchedStatuses.current) return;

    if (isOwnerOrManager) {
      if (eligibleEmployees.length > 0) {
        hasFetchedStatuses.current = true;
        fetchAllStatuses();
      } else if (hasSyncedUsers.current) {
        // Sync completed but no eligible employees
        setIsLoadingStatuses(false);
      }
    } else if (hasSyncedUsers.current) {
      // Employee role - no need to fetch all statuses
      setIsLoadingStatuses(false);
    }
  }, [
    salonLoading,
    isSyncingUsers,
    eligibleEmployees.length,
    fetchAllStatuses,
    isOwnerOrManager,
  ]);

  // Fetch timesheet data for the current pay period
  // Returns the fresh data so callers can use it immediately
  const fetchTimesheetData = useCallback(async (): Promise<TimesheetData> => {
    const companyId = typedBusinessInfo?.rollfi__company_id;
    const emptyData: TimesheetData = { timeCards: [] };

    if (!companyId || !payrollPeriod) {
      setIsLoadingTimesheet(false);
      return emptyData;
    }

    try {
      setIsLoadingTimesheet(true);
      const response = await getPayPeriodTimeCards(
        companyId,
        payrollPeriod.payPeriodId,
      );

      const freshData: TimesheetData = {
        timeCards: response.timeCards || [],
      };
      setTimesheetData(freshData);
      return freshData;
    } catch (err) {
      console.error("Error fetching timesheet data:", err);
      return emptyData;
    } finally {
      setIsLoadingTimesheet(false);
    }
  }, [
    typedBusinessInfo?.rollfi__company_id,
    payrollPeriod,
    getPayPeriodTimeCards,
  ]);

  // Fetch timesheet on initial load when payrollPeriod becomes available
  useEffect(() => {
    // Only fetch if:
    // 1. Salon has loaded
    // 2. User is owner/manager
    // 3. We have a payroll period
    // 4. We haven't already fetched this specific pay period
    if (
      !salonLoading &&
      isOwnerOrManager &&
      payrollPeriod?.payPeriodId &&
      fetchedPayPeriodId.current !== payrollPeriod.payPeriodId
    ) {
      fetchedPayPeriodId.current = payrollPeriod.payPeriodId;
      fetchTimesheetData();
    }
  }, [
    salonLoading,
    isOwnerOrManager,
    payrollPeriod?.payPeriodId,
    fetchTimesheetData,
  ]);

  // Refresh timesheet data - can be called from child components
  // Returns the fresh data so callers can use it immediately
  const handleTimesheetRefresh =
    useCallback(async (): Promise<TimesheetData> => {
      return await fetchTimesheetData();
    }, [fetchTimesheetData]);

  // Update status for a single employee - shared across tabs
  const handleStatusUpdate = useCallback(
    (employeeId: string, newStatus: TimeTrackingState) => {
      setEmployeeStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(employeeId, newStatus);
        return updated;
      });
    },
    [],
  );

  // Handle opening the modal
  const handleGetStarted = () => {
    setLegalBusinessName(typedBusinessInfo?.legal_business_name || "");
    setIsModalOpen(true);
  };

  // Handle creating the business
  const handleCreateBusiness = async () => {
    if (!legalBusinessName?.trim()) {
      notifyError("Please enter a legal business name", {
        title: "Missing Legal Name",
      });
      return;
    }

    try {
      setIsCreatingBusiness(true);

      // Update salon if legal name changed. Send ONLY the field this save
      // changes: PUT /business-locations writes every recognized key it receives, so
      // spreading the whole businessInfo record replays potentially-stale
      // values over the DB row.
      if (legalBusinessName.trim() !== typedBusinessInfo?.legal_business_name) {
        await updateSalon({
          id: typedBusinessInfo!.id,
          legal_business_name: legalBusinessName.trim(),
        });
        setBusinessInfo((prev: any) => ({
          ...prev,
          legal_business_name: legalBusinessName.trim(),
        }));
      }

      // Create RollFi business
      const businessData: CreateBusinessRequest = {
        salonId: typedBusinessInfo?.id || "",
        method: "createBusiness",
        registration: {
          company: legalBusinessName.trim(),
        },
        companyLocation: {
          companyLocation: "Main",
          address1: typedBusinessInfo?.street_address_1 || "",
          address2: typedBusinessInfo?.street_address_2 || "",
          city: typedBusinessInfo?.city || "",
          state: typedBusinessInfo?.state || "",
          zipcode: typedBusinessInfo?.zip_code || "",
          phoneNumber: `${typedBusinessInfo?.area_code || ""}${typedBusinessInfo?.phone_number || ""}`,
        },
        businessUser: {
          firstName: currentUser?.first_name || "",
          lastName: currentUser?.last_name || "",
          phoneNumber: `${currentUser?.area_code || ""}${currentUser?.phone_number || ""}`,
          email: currentUser?.email || "",
        },
      };

      const response = await createRollfiBusiness(businessData);

      if (response.companyId) {
        // Update salon with new rollfi company ID (minimal payload — see above)
        await updateSalon({
          id: typedBusinessInfo!.id,
          legal_business_name: legalBusinessName.trim(),
          rollfi__company_id: response.companyId,
        });
        setBusinessInfo((prev: any) => ({
          ...prev,
          legal_business_name: legalBusinessName.trim(),
          rollfi__company_id: response.companyId,
        }));

        // Update current user with RollFi business user ID
        if (currentUser && response.businessUserId) {
          const updatedEmployee = {
            ...currentUser,
            rollfi__user_id: response.businessUserId,
          };
          await updateStaff(updatedEmployee);

          // Update currentUser and employees list locally
          setCurrentUser(updatedEmployee);
          if (employees) {
            setEmployees(
              employees.map((emp: Employee) =>
                emp.id === currentUser.id ? updatedEmployee : emp,
              ),
            );
          }
        }

        setIsModalOpen(false);
        setShowSuccessMessage(true);
      } else {
        throw new Error(response.message || "Failed to create business");
      }
    } catch (error) {
      console.error("Error creating RollFi business:", error);
      notifyError(
        error instanceof Error
          ? error.message
          : "Failed to create business in RollFi",
        { title: "Error" },
      );
    } finally {
      setIsCreatingBusiness(false);
    }
  };

  // Block unauthorized access based on workflow permissions
  const deniedByPermissions =
    payrollPerms != null && payrollPerms.available === false;
  if (deniedByPermissions) {
    return <PayrollAccessRestricted role={currentUser?.role} />;
  }

  // Render marketing page for owners without payroll setup
  if (showMarketingPage) {
    if (showSuccessMessage) {
      return (
        <Container fluid className={styles.container} p={0}>
          <Stack gap="lg" className={styles.marketingContainer} align="center">
            <div className={styles.successIcon}>
              <IconCheck size={56} className={styles.successIconInner} />
            </div>

            <Text className={styles.successMessage}>
              Please check your email inbox for the next steps on continuing
              with Payroll!
            </Text>
          </Stack>
        </Container>
      );
    }

    return (
      <>
        <Container fluid className={styles.container} p={0}>
          <Stack gap="lg" className={styles.marketingContainer} align="center">
            <Box className={styles.backgroundBlobs}>
              <Box className={styles.rightBlob} />
            </Box>

            <Image src="/images/pagoda-logo.png" alt="Pagoda" w={120} h={120} />

            <div className={styles.badge}>Add On | Payroll</div>

            <Title order={1} fw={500} className={styles.marketingTitle}>
              Simplify Your Payroll
            </Title>

            <Stack gap={4} className={styles.marketingDescription}>
              <Text size="xl" fw={500}>
                Run payroll in minutes, not hours
              </Text>
              <Text size="xl" fw={500}>
                Seamlessly integrated with the Pagoda Platform designed to save
                you time.
              </Text>
            </Stack>

            <Button
              className={styles.ctaButton}
              rightSection={<IconArrowRight size={18} />}
              onClick={handleGetStarted}
              color="var(--color-accent)"
              size="lg"
            >
              Get Started with Payroll
            </Button>

            <div className={styles.checkmarkItems}>
              <div className={styles.checkmarkItem}>
                <IconCheck size={24} className={styles.checkIcon} />
                <Text style={{ position: "relative", top: 2 }}>
                  Dedicated success team
                </Text>
              </div>
              <div className={styles.checkmarkItem}>
                <IconCheck size={24} className={styles.checkIcon} />
                <Text style={{ position: "relative", top: 2 }}>
                  Set up in 15 minutes
                </Text>
              </div>
            </div>
          </Stack>
        </Container>

        <Modal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Get Started with Payroll"
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label={
                <Group gap={4} mb="xs">
                  <Text size="sm" fw={500}>
                    {typedBusinessInfo?.legal_business_name
                      ? "Please confirm your legal business name"
                      : "Please provide your legal business name"}
                  </Text>
                  <Text component="span" c="red" aria-hidden>
                    *
                  </Text>
                  <Tooltip
                    label={
                      <>
                        <Text c="white">
                          Your legal business name is the official name
                          registered with your state, including the
                          incorporation status (e.g., LLC, Inc, Corp)
                        </Text>
                        <Text c="white" mt="xs">
                          Example: Pagoda Salon, LLC.
                        </Text>
                      </>
                    }
                    multiline
                    w={300}
                    ml="xs"
                    withArrow
                  >
                    <IconInfoCircle
                      size={16}
                      style={{
                        color: "var(--brand-teal)",
                        cursor: "help",
                        position: "relative",
                        top: -2,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              placeholder="Enter legal business name"
              value={legalBusinessName}
              onChange={(e) => setLegalBusinessName(e.target.value)}
              required
              disabled={isCreatingBusiness}
              withAsterisk={false}
            />
            <Button
              onClick={handleCreateBusiness}
              loading={isCreatingBusiness}
              fullWidth
              color="var(--color-primary)"
            >
              Next
            </Button>
          </Stack>
        </Modal>
      </>
    );
  }

  // Not set up for this location / user: block the tabs, whose "Open Payroll
  // Portal" SSO and RollFi calls would fail. Wait for the salon and user to
  // load first (SalonContext exposes no loading flag), or a hard reload
  // would flash this for users who do have payroll.
  if (typedBusinessInfo && currentUser && !hasAccess) {
    return <PayrollAccessRestricted role={currentUser?.role} />;
  }

  // Show loading while syncing users on initial load
  if (isSyncingUsers && !hasFetchedStatuses.current) {
    return (
      <Container fluid className={styles.container}>
        <Title order={1} className={styles.pageTitle}>
          Payroll
        </Title>
        <Stack gap="md" align="center" mt="xl">
          <Text size="md" c="dimmed">
            Loading payroll data...
          </Text>
        </Stack>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.container}>
      <Title order={1} className={styles.pageTitle}>
        Payroll
      </Title>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        orientation="horizontal"
        className={styles.tabs}
      >
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconHome size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="timesheet" leftSection={<IconClock size={16} />}>
            Timesheet
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <PayrollOverviewTab
            employeeStatuses={employeeStatuses}
            isLoadingStatuses={isLoadingStatuses}
            eligibleEmployees={eligibleEmployees}
            timesheetData={timesheetData}
            onTimesheetRefresh={handleTimesheetRefresh}
          />
        </Tabs.Panel>

        <Tabs.Panel value="timesheet" pt="md">
          <PayrollTimesheetTab
            employeeStatuses={employeeStatuses}
            isLoadingStatuses={isLoadingStatuses}
            onStatusUpdate={handleStatusUpdate}
            timesheetData={timesheetData}
            isLoadingTimesheet={isLoadingTimesheet}
            onTimesheetRefresh={handleTimesheetRefresh}
            companyId={typedBusinessInfo?.rollfi__company_id || ""}
            payPeriodId={payrollPeriod?.payPeriodId || ""}
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
